// Phone niceties: keep the screen awake during a game, buzz and chime when the phone needs its
// player. Night stays silent (rulebook section 7), except that with decoys on, where every living
// phone wakes each night, a phone signals as it wakes. That departs from the rulebook by the
// owner's choice: the table can still hear the wake order (README "How a game runs").
import { useCallback, useEffect, useRef } from "react";
import { useGame } from "../state/store";

type WakeLockSentinelLike = { release(): Promise<void>; released: boolean };
type WakeLockNavigator = Navigator & {
  wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
};

/** Hold a screen wake lock while `active`; re-acquired when the page becomes visible again. */
export function useWakeLock(active: boolean): void {
  const sentinel = useRef<WakeLockSentinelLike | null>(null);
  useEffect(() => {
    const nav = navigator as WakeLockNavigator;
    if (!active || !nav.wakeLock) return;
    let cancelled = false;
    const acquire = async () => {
      if (document.visibilityState !== "visible") return;
      if (sentinel.current && !sentinel.current.released) return;
      try {
        const lock = await nav.wakeLock!.request("screen");
        if (cancelled) void lock.release();
        else sentinel.current = lock;
      } catch {
        /* refused (battery saver, not HTTPS): the screen may sleep, the game still works */
      }
    };
    void acquire();
    document.addEventListener("visibilitychange", acquire);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", acquire);
      void sentinel.current?.release().catch(() => undefined);
      sentinel.current = null;
    };
  }, [active]);
}

/**
 * Whether a signal for a moment by day (`atNight` false) or a night wake (`atNight` true) may play
 * now. A day signal never plays at night, and a night one needs decoys on and a night still going.
 */
function signalAllowed(atNight: boolean): boolean {
  const view = useGame.getState().view;
  const night = view?.phase === "night" || view?.phase === "first_night";
  return atNight ? night && !!view?.config.night_decoys : !night;
}

// Android takes a vibration of 1 s or less from a web page for touch feedback, which many phones
// turn off (VibratorManagerService, MAX_HAPTIC_FEEDBACK_DURATION), so every pulse is longer.
export const PULSE_MS = 1100;
const GAP_MS = 400;

let pulseTimers: number[] = [];

/** One pulse, if this phone can buzz now: supported (Android), switched on, allowed, tapped. */
function pulse(atNight: boolean): boolean {
  if (typeof navigator.vibrate !== "function") return false;
  if (!useGame.getState().prefs.haptics || !signalAllowed(atNight)) return false;
  // until the page is tapped (say, after a reload) Chrome refuses and logs an error
  if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return false;
  try {
    return navigator.vibrate(PULSE_MS);
  } catch {
    return false;
  }
}

/**
 * Buzz `pulses` times for a moment by day, or for a night wake (`atNight`), respecting the setting.
 * Each pulse is its own call, checked as it starts: given a pattern, Chrome would play the rest of
 * a day's buzz after night fell.
 */
export function buzz(pulses: number, atNight = false): void {
  for (const t of pulseTimers) window.clearTimeout(t);
  pulseTimers = [];
  if (!pulse(atNight)) return;
  for (let i = 1; i < pulses; i++) {
    pulseTimers.push(window.setTimeout(() => pulse(atNight), i * (PULSE_MS + GAP_MS)));
  }
}

let audio: AudioContext | null = null;

/** Create the AudioContext inside a user gesture so browsers allow it to play later. */
export function unlockAudio(): void {
  if (audio) return;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;
  try {
    audio = new Ctor();
  } catch {
    audio = null;
  }
}

/** A short two-note chime, off unless enabled, at night only for a wake (see `buzz`). */
export function useChime() {
  const enabled = useGame((s) => s.prefs.sound);
  return useCallback(
    (atNight = false) => {
      if (!enabled || !signalAllowed(atNight) || !audio) return;
      const ctx = audio;
      void ctx.resume().catch(() => undefined);
      const start = ctx.currentTime;
      [660, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.0001, start + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.18, start + i * 0.12 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + i * 0.12 + 0.25);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start + i * 0.12);
        osc.stop(start + i * 0.12 + 0.3);
      });
    },
    [enabled],
  );
}
